import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import ClientSidebar from '../components/Dashboard/ClientSidebar';
import { userAPI, getFullUrl } from '../services/api';

const CoachProfile = () => {
  const { coachId } = useParams();
  const { user: currentUser } = useAuth();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getPersonalizedDescription = (coach, userName) => {
    if (!coach) return '';
    const description = coach.description || 'Dedicated to helping you achieve your full potential through personalized AI-powered coaching.';
    const greeting = `Welcome to Ally, ${userName || 'friend'}!`;
    
    // Attempt to personalize if common phrases are found, otherwise just return description
    return description
      .replace('Introducing Ally, Your AI Coaching Companion!', greeting)
      .replace('Hello, I\'m Michael Legge.', greeting)
      .replace('Hello! I\'m Ashley Copley', greeting);
  };

  useEffect(() => {
    const fetchCoach = async () => {
      try {
        setLoading(true);
        const response = await userAPI.getById(coachId);
        // Backend returns { user: { ... } }
        setCoach(response.data.user);
      } catch (err) {
        console.error('Error fetching coach:', err);
        setError('Coach not found');
      } finally {
        setLoading(false);
      }
    };
    
    if (coachId) {
      fetchCoach();
    }
  }, [coachId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--bg-main)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--tangerine)]"></div>
      </div>
    );
  }

  if (error || !coach) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[var(--bg-main)] p-6 text-center">
        <span className="material-symbols-outlined text-6xl text-gray-300 mb-4">person_off</span>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Coach not found</h2>
        <p className="text-gray-500 mb-8">The coach you are looking for does not exist or you don't have access.</p>
        <Link to="/user/coaches" className="bg-[var(--tangerine)] text-white px-8 py-3 rounded-2xl font-bold shadow-lg shadow-orange-500/20">
          Back to Coaches
        </Link>
      </div>
    );
  }

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 flex flex-1 justify-center py-5 w-full">
          <div className="layout-content-container flex flex-col lg:flex-row w-full max-w-[1200px] flex-1 gap-8">
            
            <main className="flex-1 flex flex-col">
              <header className="mb-8 flex items-center justify-between">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] tracking-tight mb-2">Coach Profile</h1>
                  <nav className="flex items-center gap-2 text-xs font-bold text-[var(--text-secondary)]/60 uppercase tracking-widest">
                    <Link to="/user/coaches" className="hover:text-[var(--tangerine)] transition-colors">Coaches</Link>
                    <span className="material-symbols-outlined text-sm">chevron_right</span>
                    <span className="text-[var(--text-primary)]">{coach.name}</span>
                  </nav>
                </div>
              </header>

              <div className="space-y-8">
                {/* Hero Profile Card */}
                <div className="bg-[var(--surface-white)] rounded-[3rem] shadow-2xl shadow-black/10 border border-[var(--border-light)] overflow-hidden relative group">
                  <div className="p-10 sm:p-12 relative">
                    <div className="flex flex-col sm:flex-row gap-10 items-start">
                       {/* Enhanced Avatar Frame */}
                       <div className="w-40 h-40 rounded-[2.5rem] overflow-hidden border-8 border-white shadow-[0_20px_50px_rgba(0,0,0,0.1)] shrink-0 bg-[var(--off-white)] relative z-10 transition-transform duration-500 group-hover:scale-[1.02]">
                          {coach.avatarUrl ? (
                            <img 
                              src={getFullUrl(coach.avatarUrl)}
                              alt={coach.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                               <span className="material-symbols-outlined text-6xl">person</span>
                            </div>
                          )}
                       </div>
                       
                       <div className="flex-1 min-w-0">
                          <div className="mb-6">
                            <h2 className="text-4xl font-black text-[var(--text-primary)] mb-1 tracking-tight">{coach.name}</h2>
                            <p className="text-[var(--tangerine)] font-extrabold text-xs uppercase tracking-[0.3em]">{coach.specialization || "Expert Coach"}</p>
                          </div>
                          
                          {/* Elegant Quote Callout */}
                          <div className="relative mb-10 px-8 py-6 bg-[var(--tangerine)]/10 border-[var(--tangerine)]/20">
                             <span className="absolute -top-3 -left-1 text-6xl text-[var(--tangerine)]/20 font-serif opacity-50 select-none">“</span>
                             <p className="text-[var(--text-secondary)] leading-relaxed italic text-lg relative z-10">
                                {coach.description ? coach.description.split('.')[0] + '.' : "I am here to support your growth."}
                             </p>
                             <span className="absolute -bottom-10 -right-2 text-6xl text-[var(--tangerine)]/20 font-serif opacity-50 select-none">”</span>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
                             <Link to={`/user/chat/${coach.id}`} className="bg-[var(--tangerine)] text-white rounded-2xl font-bold shadow-lg shadow-orange-500/20 hover:scale-105 transition-all flex items-center justify-center gap-2 py-4 text-sm sm:text-base">
                                <span className="material-symbols-outlined">forum</span>
                                Start Chat
                             </Link>
                             <Link to={`/user/voice/${coach.id}`} className="bg-[var(--surface-white)] border border-[var(--border-light)] text-gray-700 rounded-2xl font-bold shadow-sm hover:border-[var(--tangerine)] hover:text-[var(--tangerine)] transition-all flex items-center justify-center gap-2 py-4 text-sm sm:text-base">
                                <span className="material-symbols-outlined">mic</span>
                                Voice Session
                             </Link>
                             <Link to={`/user/video/${coach.id}`} className="bg-[var(--surface-white)] border border-[var(--border-light)] text-gray-700 rounded-2xl font-bold shadow-sm hover:border-[var(--tangerine)] hover:text-[var(--tangerine)] transition-all flex items-center justify-center gap-2 py-4 text-sm sm:text-base">
                                <span className="material-symbols-outlined">videocam</span>
                                Video Session
                             </Link>
                          </div>
                       </div>
                    </div>
                  </div>
                </div>

                {/* About Section */}
                <section className="bg-[var(--surface-white)] rounded-[2.5rem] shadow-xl shadow-black/5 border border-[var(--border-light)] p-8 md:p-12">
                   <h3 className="text-xl font-bold text-[var(--text-primary)] mb-6 flex items-center gap-2">
                      <span className="material-symbols-outlined text-[var(--tangerine)]">info</span>
                      Coaching Philosophy
                   </h3>
                   <div className="text-[var(--text-secondary)] space-y-4 leading-relaxed whitespace-pre-line text-sm md:text-base max-w-4xl">
                      {getPersonalizedDescription(coach, currentUser?.name)}
                   </div>
                </section>
              </div>
            </main>

            <ClientSidebar selectedCoach={coach} activeTab="profile" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachProfile;
